<template>
  <g :transform="`translate(${marginLeft}, 0)`">
    <path  v-for="(line, index) in lines"
          :key="index"
          :class="className"
          :d="line.path"
          :fill="line.fill"
          :stroke="line.stroke"
    />
    <Verticals v-if="showVerticals"
      v-bind="$props"
      :xScale="xScale"
      @onHover="onHover"
      @onLeave="onLeave"/>
    <Dots v-if="showDots"
      v-bind="$props"
      :metrics="metrics"
      :xScale="xScale"
      :yScale="yScale"
      @onHover="onHover"
      @onLeave="onLeave"/>
  </g>
</template>

<script>
import { bandScale, timeSeriesScale, linearScale }
  from '../../../../../../../services/dashboard/graphs/scalesService.js';
import { line, area } from 'd3-shape';
import Verticals from './Verticals';
import Dots from './Dots';

export default {
  props: {
    // From Graph Wrapper
    data: {
      type: Array,
      required: true,
    },
    defaultChartMax: {
      type: Number,
    },
    dimensionKey: {
      type: String,
      required: true,
    },
    contentDimensions: {
      type: Object,
      required: true,
    },
    marginTop: {
      type: Number,
      default: 0,
    },
    marginLeft: {
      type: Number,
      default: 0,
    },
    // Custom to Line Chart
    customChartMax: {
      type: Number,
    },
    scale: {
      type: String,
      default: 'timeSeries',
      validator: v => {
        return ['timeSeries', 'band'].indexOf(v) > -1;
      },
    },
    height: {
      type: Number,
      required: true,
    },
    fillArea: {
      type: Boolean,
      default: false,
    },
    showVerticals: {
      type: Boolean,
      default: true,
    },
    showDots: {
      type: Boolean,
      default: true,
    },
    metric: {
      type: String,
    },
    colors: {
      type: Array,
      default: () => (['#4369F4', '#233786', '#00A6A4']),
    },
    hoveredIndex: {
      type: Number,
    },
    hideOnLeave: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Verticals,
    Dots,
  },
  computed: {
    chartMax() {
      return this.customChartMax || this.defaultChartMax;
    },
    xScale() {
      const range = { min: 0, max: this.contentDimensions.width };
      const args = [ this.data, this.dimensionKey, range ];

      switch (this.scale) {
        case 'band':
          return bandScale(...args);
        case 'timeSeries':
        default:
          return timeSeriesScale(...args);
      }

    },
    yScale() {
      const range = { min: this.contentDimensions.height, max: this.marginTop };
      const domain = { min: 0, max: this.chartMax };

      return linearScale(range, domain);
    },
    metrics() {
      if (this.metric) {
        return [{
          name: this.metric,
          color: this.colors[0],
        }];
      }

      if (this.data && this.data[0]) {
        const keys = Object.keys(this.data[0]) || [];
        const metricKeys = keys.filter(k => k !== this.dimensionKey);

        return metricKeys.map((mk, index) => {
          const color = this.colors[index % this.colors.length];

          return {
            name: mk,
            color: color,
          };
        });
      }

      return [];
    },
    className() {
      return this.fillArea ? 'area' : 'line';
    },
    lines() {
      return this.metrics.map((mk) => {
        const color = mk.color;
        const name = mk.name;

        const fill = this.fillArea ? color : 'none';
        const stroke = this.fillArea ? 'none' : color;
        const pathFunction = this.fillArea ? this.metricAreaPath(name) : this.metricLinePath(name);

        return {
          path: pathFunction(this.data),
          fill,
          stroke,
        };
      });
    },
  },
  methods: {
    metricAreaPath(metricName) {
      return area()
        .x(d => this.xScale(d[this.dimensionKey]))
        .y0(this.height)
        .y1(d => this.yScale(d[metricName]));
    },
    metricLinePath(metricName) {
      return line()
        .x(d => this.xScale(d[this.dimensionKey]))
        .y(d => this.yScale(d[metricName]));
    },
    onHover(val) {
      this.$emit('onHover', val);
    },
    onLeave() {
      this.$emit('onLeave');
    },
  },
};
</script>
<style scoped>
path.line {
  fill: none;
  stroke-width: 2px;
}

path.area {
  fill-opacity: 0.5;
  stroke: none;
}

.grid line {
  opacity: 0.05;
  stroke: #C1CCD4;
}

</style>
